/**
 * Module summary:
 * This is the top-level route map for the browser app. It separates public and
 * protected pages, chooses the correct landing route, and wraps authenticated
 * pages with the shared authorization guard.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './components/pages/LoginPage';
import { AssessmentWorkspace } from './components/pages/AssessmentWorkspace';
import { AuthGuard } from './services/auth/AuthGuard';
import { useAuth } from './hooks/useAuth';
import { HomeScreen } from './components/pages/HomeScreen';
import { UserProfile } from './components/pages/UserProfile';
import { CreateAccount } from './components/pages/CreateAccount';
import { ForgotPassword } from './components/pages/ForgotPassword';
import { PastAssessments } from './components/pages/PastAssessments';
import { AboutUs } from './components/pages/AboutUs';
import { CreateAssessment } from './components/pages/CreateAssessment';
import { ReportView } from './components/pages/ReportView';
import { ContactUs } from './components/pages/ContactUs';
import { MfaSetup } from './components/pages/MfaSetup';
import { EmailActionHandler } from './components/pages/EmailActionHandler';
import { AdminAssessmentImport } from './components/pages/AdminAssessmentImport';
import { VerifyEmailPending } from './components/pages/VerifyEmailPending';
import { requiresProfileCompletion } from './services/auth/authService';
import './App.css';

function Protected({ children }: { children: React.ReactNode }) {
  // Route all protected page elements through one guard so auth and MFA rules stay centralized.
  return <AuthGuard>{children}</AuthGuard>;
}

function RootRoute() {
  const { loading, isAuthenticated, user } = useAuth();

  if (loading) {
    return (
      <div className="auth-guard__loading">
        <div className="auth-guard__spinner" />
        <p>Checking authentication...</p>
      </div>
    );
  }

  // Send signed-in users to the dashboard and everyone else to the login screen.
  return <Navigate to={isAuthenticated ? (requiresProfileCompletion(user) ? '/profile' : '/home') : '/login'} replace />;
}

function App() {
  return (
    <div className="app">
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/create-account" element={<CreateAccount />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/mfa-setup" element={<MfaSetup />} />
        <Route path="/verify-email" element={<VerifyEmailPending />} />
        <Route path="/auth/action" element={<EmailActionHandler />} />

        {/* Protected routes */}
        <Route path="/home" element={<Protected><HomeScreen /></Protected>} />
        <Route path="/assessment" element={<Protected><AssessmentWorkspace /></Protected>} />
        <Route path="/reports" element={<Protected><PastAssessments /></Protected>} />
        <Route path="/report/:id" element={<Protected><ReportView /></Protected>} />
        <Route path="/create-assessment" element={<Protected><CreateAssessment /></Protected>} />
        <Route path="/profile" element={<Protected><UserProfile /></Protected>} />
        <Route path="/about" element={<Protected><AboutUs /></Protected>} />
        <Route path="/contact" element={<Protected><ContactUs /></Protected>} />
        <Route path="/admin/import-assessment" element={<Protected><AdminAssessmentImport /></Protected>} />

        {/* Redirects for old routes */}
        <Route path="/past-assessments" element={<Navigate to="/reports" replace />} />
        <Route path="/" element={<RootRoute />} />
      </Routes>
    </div>
  );
}

export default App;
